import React, { Fragment, useState, useEffect, useMemo, useCallback } from 'react'
import { requestTranslation, radarLanguagesWithAll, setLanguage } from '@futures-platform/i18n'
import { OptionDropdown, OptionDropdownCustomedForTypeFilterOnContentExpl, TagOptionDropdown, TimelineOptionDropdown } from '@futures-platform/ui'
import { useTags, usePhenomenonTypes, useGroups } from '@futures-platform/hooks'
import { useDebounce } from 'use-debounce'
import { addOrRemoveValueFromArray, getTypeLabel, getTagLabel } from './helpers'
import { map, concat, find, filter } from 'lodash-es'

const PUBLIC_GROUP = { value: 0, label: requestTranslation('publicWord') }
const USER_LANGUAGE = document.querySelector('html').getAttribute('lang') || 'en'
const SELECTED_LANGUAGE = find(radarLanguagesWithAll(), { value: USER_LANGUAGE })
const DEFAULT_TIMES = { min: new Date().getFullYear(), max: null }
const SEARCH_DEBOUNCE_MS = 350

setLanguage(USER_LANGUAGE)

const ContentFilters = ({
  onFilterChange,
  page,
  search,
  passedGroups = false,
  countShown = true,
  resetShown = true,
  manualFilterReset,
  groupsProp,
  groupsLoading,
  highest_group_role,
  isRadar,
  group,
  langRadar,
  isFilteredProp,
  isHideTypeFilter,
  isHideTimeFilter,
  isHideTagFilter,
  isHideGroupFilter,
  isHideLangFilter,
  isHideResetFilterBtn
}) => {
  const hasPassedGroups = useMemo(() => groupsProp && groupsProp !== undefined, [groupsProp])

  const [typesShown, setTypesShown] = useState(false)
  const [timesShown, setTimesShown] = useState(false)
  const [tagsShown, setTagsShown] = useState(false)
  const [groupsShown, setGroupsShown] = useState(false)
  const [languagesShown, setLanguagesShown] = useState(false)
  const [isFiltered, setIsFiltered] = useState(isFilteredProp)
  const [selectedTypes, setSelectedTypes] = useState([])
  const [selectedTimes, setSelectedTimes] = useState(DEFAULT_TIMES)
  const [selectedTags, setSelectedTags] = useState([])
  const [selectedLanguage, setSelectedLanguage] = useState(!!isRadar ? {
    value: langRadar || USER_LANGUAGE,
    label: langRadar === 'en' ? 'English' : 'Suomi'
  } : SELECTED_LANGUAGE)
  
  const [debouncedSearchValue] = useDebounce(search, SEARCH_DEBOUNCE_MS)

  // Initialize selectedGroup based on whether groups are passed
  const initializeSelectedGroup = useCallback(() => {
    if (hasPassedGroups) {
      // For the initial selected group, use the first item from passedGroups that has label "All"
      const allGroup = Array.isArray(passedGroups) 
        ? passedGroups.find(g => g.label === requestTranslation('allWord') || g.label === 'All')
        : null
      
      if (allGroup) {
        return allGroup
      }
      
      // Fallback: create an "All" group
      const allGroups = groupsProp?.map(g => g.value) || []
      const allGroupValues = concat([PUBLIC_GROUP.value], allGroups)
      return { value: allGroupValues, label: requestTranslation('allWord') }
    }
    return PUBLIC_GROUP
  }, [hasPassedGroups, passedGroups, groupsProp])

  const [selectedGroup, setSelectedGroup] = useState(initializeSelectedGroup())

  // Initialize hooks
  const { loading: groupsLoadingHook, groups } = useGroups()
  const actualGroupsLoading = groupsLoading || groupsLoadingHook
  
  const {
    loading: phenomenaTypesLoading,
    phenomenonTypes
  } = usePhenomenonTypes(selectedGroup.value)

  // Calculate loading state after all hooks are initialized
  const loading = phenomenaTypesLoading || actualGroupsLoading

  // Memoize phenomenon type options
  const phenomenonTypeOptions = useMemo(() => 
    map(phenomenonTypes, type => ({
      value: type.id,
      label: type.alias || type.title || type.label,
      style: type.style
    })), [phenomenonTypes]
  )

  // Initialize group options based on whether groups are passed
  const getGroupOptions = useMemo(() => {
    if (hasPassedGroups) {
      const sortedGroups = filter(groupsProp, group => group.label).sort((a, b) => {
        if (a?.label < b?.label) return -1
        if (a?.label > b?.label) return 1
        return 0
      })
      
      // Include passedGroups if they exist and are valid
      const validPassedGroups = Array.isArray(passedGroups) 
        ? passedGroups.filter(group => group && typeof group === 'object' && group.value !== undefined)
        : []
      
      // Combine all groups, placing sortedGroups after the public group (value: 0)
      const publicGroupIndex = validPassedGroups.findIndex(g => g.value === 0)
      let allOptions = []

      if (publicGroupIndex !== -1) {
        // If public group is found, insert sortedGroups after it
        const before = validPassedGroups.slice(0, publicGroupIndex + 1)
        const after = validPassedGroups.slice(publicGroupIndex + 1)
        allOptions = [...before, ...sortedGroups, ...after]
      } else {
        // Fallback: if no public group, behave as before
        allOptions = [...validPassedGroups, ...sortedGroups]
      }

      const uniqueOptions = allOptions.filter((option, index, self) => 
        index === self.findIndex(item => {
          // Handle array values for "All" option
          if (Array.isArray(item.value) && Array.isArray(option.value)) {
            return JSON.stringify(item.value.sort()) === JSON.stringify(option.value.sort())
          }
          return item.value === option.value
        })
      )
      
      return uniqueOptions
    }
    
    const filteredGroups = filter(groups, group => group.id).map(group => ({
      value: group.id,
      label: group.label || group.title
    }))
    
    // Handle passedGroups - ensure they're in the correct format
    const normalizedPassedGroups = Array.isArray(passedGroups) 
      ? passedGroups.filter(group => group && typeof group === 'object' && group.value !== undefined)
      : []
    
    const allOptions = [...normalizedPassedGroups, PUBLIC_GROUP, ...filteredGroups]
    
    // Deduplicate by value property
    return allOptions.filter((option, index, self) => 
      index === self.findIndex(item => item.value === option.value)
    )
  }, [hasPassedGroups, groupsProp, passedGroups, groups])

  const groupOptions = getGroupOptions
  
  const resetFilters = useCallback(() => {
    setSelectedTypes([])
    setSelectedTimes(DEFAULT_TIMES)
    setSelectedTags([])
    setSelectedLanguage(!!isRadar ? {
      value: langRadar || USER_LANGUAGE,
      label: langRadar === 'en' ? 'English' : 'Suomi'
    } : SELECTED_LANGUAGE)
    setSelectedGroup(hasPassedGroups ? initializeSelectedGroup() : PUBLIC_GROUP)
  }, [isRadar, langRadar, hasPassedGroups, initializeSelectedGroup])

  // Memoized event handlers
  const handleTypeSelect = useCallback((type) => {
    setSelectedTypes(addOrRemoveValueFromArray(selectedTypes, type))
    !isRadar && setIsFiltered(true)
  }, [selectedTypes, isRadar])

  const handleTimeSelect = useCallback((times) => {
    setSelectedTimes(times)
    !isRadar && setIsFiltered(true)
  }, [isRadar])

  const handleTagSelect = useCallback((tag) => {
    let tempTag = null
    if (tag?.uri) {
      tempTag = {
        label: tag?.label,
        value: tag?.uri
      }
    }
    !isRadar && setIsFiltered(true)
    setSelectedTags(addOrRemoveValueFromArray(selectedTags, tempTag || tag))
  }, [selectedTags, isRadar])

  const handleGroupSelect = useCallback((e) => {
    setSelectedGroup(find(groupOptions, { label: e.target.textContent || e.target.innerText }))
    !isRadar && setIsFiltered(true)
  }, [groupOptions, isRadar])

  const handleLanguageSelect = useCallback((e) => {
    !isRadar && setIsFiltered(true)
    setSelectedLanguage(find(radarLanguagesWithAll(), { label: e.target.innerText }))
  }, [isRadar])

  // Memoize expensive calculations
  const selectedTypesLength = useMemo(() => selectedTypes.length, [selectedTypes.length])
  const selectedTagsLength = useMemo(() => selectedTags.length, [selectedTags.length])
  const typeTitle = useMemo(() => 
    selectedTypesLength ? getTypeLabel(selectedTypes) : requestTranslation('all'), 
    [selectedTypesLength, selectedTypes]
  )
  const timeTitle = useMemo(() => 
    `${selectedTimes.min || ''} - ${selectedTimes.max || ''}`, 
    [selectedTimes.min, selectedTimes.max]
  )
  const tagTitle = useMemo(() => 
    selectedTagsLength === 0 ? requestTranslation('none') : getTagLabel(selectedTags, selectedLanguage),
    [selectedTagsLength, selectedTags, selectedLanguage]
  )

  const areFiltersApplied = useCallback(() => {
    const defaultGroup = hasPassedGroups ? initializeSelectedGroup() : PUBLIC_GROUP
    if (selectedTypes.length || 
        selectedTimes !== DEFAULT_TIMES || 
        selectedTags.length || 
        selectedLanguage !== SELECTED_LANGUAGE || 
        selectedGroup !== defaultGroup) {
      return true
    }
    return false
  }, [hasPassedGroups, initializeSelectedGroup, selectedTypes.length, selectedTimes, selectedTags.length, selectedLanguage, selectedGroup])

  useEffect(() => {
    if (!isRadar) {
      setIsFiltered(isFilteredProp)
    }
  }, [isFilteredProp])

  useEffect(() => {
    const filterData = {
      types: selectedTypes,
      times: selectedTimes,
      tags: selectedTags,
      language: selectedLanguage,
      group: selectedGroup,
      page,
      search: debouncedSearchValue,
      filtersActive: areFiltersApplied()
    }

    if (!isRadar) {
      filterData.isFiltered = isFiltered
    }

    onFilterChange(filterData)
  }, [
    selectedTypes,
    selectedTimes,
    selectedTags,
    selectedLanguage,
    selectedGroup,
    page,
    debouncedSearchValue,
    isFiltered
  ])

  useEffect(() => {
    if (manualFilterReset) {
      resetFilters()
    }
  }, [manualFilterReset])

  return (
    <Fragment>
      {loading && <div className="py-2 pl-2">{requestTranslation('loading')}</div>}
      
      <div className='mb-3' id={highest_group_role === 'free' ? "type-cont-exp-filter" : null}>
        {!isHideTypeFilter && (
          <OptionDropdownCustomedForTypeFilterOnContentExpl
            label={requestTranslation('createPhenomenaFormTypeLabel')}
            optionsShown={typesShown}
            type={'type'}
            title={typeTitle}
            selectedOption={selectedTypes}
            handleOptionSelect={handleTypeSelect}
            options={phenomenonTypeOptions}
            onTabClick={() => setTypesShown(!typesShown)}
            resetFilters={() => setSelectedTypes([])}
            countShown={countShown}
            highest_group_role={highest_group_role}
            disabled={highest_group_role === 'free'}
          />
        )}
      </div>

      <div className='mb-3' id={highest_group_role === 'free' ? "time-cont-exp-filter" : null}>
        {!isHideTimeFilter && (
          <TimelineOptionDropdown
            label={requestTranslation('time')}
            optionsShown={timesShown}
            title={timeTitle}
            selectedOption={selectedTimes}
            handleOptionSelect={handleTimeSelect}
            onTabClick={() => setTimesShown(!timesShown)}
            countShown={countShown}
            highest_group_role={highest_group_role}
            disabled={highest_group_role === 'free'}
          />
        )}
      </div>

      <div className='mb-3' id={highest_group_role === 'free' ? "tags-cont-exp-filter" : null}>
        {!isHideTagFilter && (
          <TagOptionDropdown
            isFilteredProp={isFilteredProp}
            isRadar={isRadar}
            label={requestTranslation('tags')}
            optionsShown={tagsShown}
            title={tagTitle}
            selectedOption={selectedTags}
            handleOptionSelect={handleTagSelect}
            onTabClick={() => setTagsShown(!tagsShown)}
            group={selectedGroup.value}
            language={selectedLanguage.value}
            countShown={countShown}
            useTags={useTags}
            highest_group_role={highest_group_role}
            disabled={highest_group_role === 'free'}
            groupFromRadarApp={group}
            nameRadio="tag-filters"
          />
        )}
      </div>

      <div className='mb-3' id={highest_group_role === 'free' ? "group-cont-exp-filter" : null}>
        {!isHideGroupFilter && (
          <OptionDropdown
            label={requestTranslation("sources")}
            title={selectedGroup.label}
            type={'radio'}
            optionsShown={groupsShown}
            options={groupOptions}
            selectedOption={selectedGroup}
            onTabClick={() => setGroupsShown(!groupsShown)}
            handleOptionSelect={handleGroupSelect}
            countShown={countShown}
            highest_group_role={highest_group_role}
            disabled={highest_group_role === 'free'}
            nameRadio="group-filters"
          />
        )}
      </div>

      <div className='mb-3' id={highest_group_role === 'free' ? "lang-cont-exp-filter" : 'lang-filters'}>
        {!isHideLangFilter && (
          <OptionDropdown
            label={requestTranslation('language')}
            title={selectedLanguage.label}
            type={'radio'}
            optionsShown={languagesShown}
            options={radarLanguagesWithAll()}
            selectedOption={selectedLanguage}
            handleOptionSelect={handleLanguageSelect}
            onTabClick={() => setLanguagesShown(!languagesShown)}
            countShown={countShown}
            highest_group_role={highest_group_role}
            disabled={highest_group_role === 'free'}
            nameRadio="lang-filters"
          />
        )}
      </div>

      {resetShown && !isHideResetFilterBtn && (
        <button 
          style={{ opacity: highest_group_role === 'free' ? 0.5 : 1 }}
          disabled={highest_group_role === 'free'}
          className='btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center' 
          onClick={resetFilters}
        >
          <i className='material-icons mr-1' style={{ fontSize: '16px' }}>replay</i>
          {requestTranslation('resetFilters')}
        </button>
      )}
    </Fragment>
  )
}

const MemoizedContentFilters = React.memo(ContentFilters, (prevProps, nextProps) => {
  // Custom comparison for better performance
  const keysToCompare = [
    'page', 'search', 'passedGroups', 'countShown', 'resetShown', 
    'manualFilterReset', 'groupsProp', 'groupsLoading', 'highest_group_role',
    'isRadar', 'group', 'langRadar', 'isFilteredProp'
  ]
  
  return keysToCompare.every(key => {
    if (key === 'passedGroups' || key === 'groupsProp') {
      return JSON.stringify(prevProps[key]) === JSON.stringify(nextProps[key])
    }
    return prevProps[key] === nextProps[key]
  })
})

export default MemoizedContentFilters
